import React, { useState } from 'react';
import { css } from '@emotion/react';
import {
  Flex,
  Button,
  Alert,
  Link,
  Text,
  theme,
  Panel,
} from '@xometry/xometry_loft';
import ShippingAccountTable, {
  ShippingAccountsTableProps,
} from './ShippingAccountTable';
import EditShippingAccountForm from './EditShippingAccountForm';
import { entityTypeEnum, userRoleEnum } from '../ShippingAddress/types';

const UserShippingAccounts = ({
  entityID,
  entityType,
  shippingAccounts,
  approved3rdPartyShipping = false,
  refreshAuth,
  userRole,
  onOnboardingActionCompleted,
}: {
  entityID: string; // this can be a user/team ID
  entityType: entityTypeEnum;
  shippingAccounts: ShippingAccountsTableProps[] | [];
  approved3rdPartyShipping: boolean;
  refreshAuth: () => void;
  userRole: userRoleEnum | null;
  onOnboardingActionCompleted: () => void;
}) => {
  const [newAccountView, setNewAccountView] = useState(false);
  const [editShippingAccountIndex, setEditShippingAccountIndex] = useState(-1);
  const addAccountLink = (
    <Flex>
      <Button link onClick={() => setNewAccountView(true)}>
        <i className="far fa-plus-circle" /> Add Shipping Account
      </Button>
    </Flex>
  );

  const onEditClick = (index: number) => {
    setEditShippingAccountIndex(index);
    setNewAccountView(true);
  };
  const exitEditMode = () => {
    setEditShippingAccountIndex(-1);
    setNewAccountView(false);
  };

  const notAuthorized = (
    <Alert
      kind={theme.kind.primary}
      border={`1px solid ${theme.colors.blues[100]}`}
    >
      <Flex flexDirection="column" padding={theme.space[2]}>
        <Text kind="body-title">
          Already have a shipping account with a carrier?
        </Text>
        <Text>
          If you would like to use your own shipping account, contact{' '}
          <Link fontSize={theme.fontSizes[2]} href="mailto:support@xometry.com">
            support@xometry.com
          </Link>
        </Text>
      </Flex>
    </Alert>
  );
  const noAccounts = (
    <Alert
      kind={theme.kind.primary}
      border={`1px solid ${theme.colors.blues[100]}`}
    >
      <Flex flexDirection="column" padding={theme.space[2]}>
        <Text kind="body-title">
          Already have a shipping account with a carrier?
        </Text>
        <Text marginBottom={theme.space[4]}>
          Add that account number to your settings and we will apply all of your
          shipping charges directly to that account.
        </Text>
        {addAccountLink}
      </Flex>
    </Alert>
  );

  const shouldShowAddAccountLink =
    shippingAccounts?.length > 0 &&
    !newAccountView &&
    (entityType === entityTypeEnum.user || userRole === userRoleEnum.admin);

  return (
    <>
      <Flex>
        {userRole && userRole === userRoleEnum.contributor && (
          <Alert kind={theme.kind.primary}>
            <i
              className="far fa-info-circle"
              css={css`
                color: ${theme.colors.blues[100]};
                margin-left: 4px;
                margin-right: ${theme.space[1]};
                font-size: 13px;
              `}
            />
            Contact a Team Admin to update team shipping account.
          </Alert>
        )}
      </Flex>
      <Flex justifyContent="space-between" alignContent="center">
        {shouldShowAddAccountLink && addAccountLink}
      </Flex>
      {/**
       * We only want to render the panel below if not contributor OR contributor and some shipping accounts
       */}
      {(userRole !== userRoleEnum.contributor ||
        (userRole === userRoleEnum.contributor &&
          shippingAccounts?.length > 0)) && (
        <Panel kind="primary" marginTop={theme.space[4]}>
          <Flex flexDirection="column" width="100%">
            {newAccountView ? (
              <EditShippingAccountForm
                entityID={entityID}
                entityType={entityType}
                exitEditMode={exitEditMode}
                refreshAuth={refreshAuth}
                shippingAccountsLength={shippingAccounts.length}
                accountToEdit={
                  editShippingAccountIndex >= 0
                    ? shippingAccounts[editShippingAccountIndex]
                    : null
                }
                isEditMode={editShippingAccountIndex >= 0}
                onOnboardingActionCompleted={onOnboardingActionCompleted}
              />
            ) : (
              <Flex>
                {shippingAccounts?.length === 0 &&
                  approved3rdPartyShipping &&
                  noAccounts}
                {!approved3rdPartyShipping && notAuthorized}
                {shippingAccounts?.length > 0 && approved3rdPartyShipping && (
                  <ShippingAccountTable
                    entityID={entityID}
                    entityType={entityType}
                    shippingAccounts={shippingAccounts}
                    refreshAuth={refreshAuth}
                    onEditClick={onEditClick}
                    userRole={userRole}
                  />
                )}
              </Flex>
            )}
          </Flex>
        </Panel>
      )}
    </>
  );
};

export default UserShippingAccounts;
